import { JsonProperty, Serializable } from 'ts-jackson'
import { Fromable } from '../model'

export enum ProductStatus {
  ALL = 'ALL',
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export enum ProductPaymentType {
  CASH = 'CASH',
  CREDIT = 'CREDIT',
}

export enum ProductUnit {
  PIECE = 'PIECE',
  BOX = 'BOX',
  PACK = 'PACK',
  BOTTLE = 'BOTTLE',
  BAG = 'BAG',
  BAR = 'BAR',
  PAIR = 'PAIR',
  SESSION = 'SESSION',
  DAY = 'DAY',
}

export enum ReceiptPaymentMethod {
  CASH = 'CASH',
  CARD = 'CARD',
  TRANSFER = 'TRANSFER',
}

export enum PaymentPlan {
  FULL_AMOUNT = 'FULL_AMOUNT',
  INSTALLMENT = 'INSTALLMENT',
}

export enum TaxBusinessType {
  COMPANY = 'COMPANY',
  LIMITED_PARTNERSHIP = 'LIMITED_PARTNERSHIP',
  INDIVIDUAL = 'INDIVIDUAL',
}

export enum TaxIdType {
  NATIONAL_ID = 'NATIONAL_ID',
  PASSPORT = 'PASSPORT',
}

export type DiscountType = 'AMOUNT' | 'PERCENT'
export type ProductSaleStatus = 'COMPLETED' | 'VOIDED'

export interface PageResult<T> {
  data: T[]
  pagination: {
    page: number
    pageSize: number
    total: number
    totalPages: number
  }
}

@Serializable()
export class ProductCategoryEntity extends Fromable {
  @JsonProperty('id')
  id!: string

  @JsonProperty('name')
  name!: string

  @JsonProperty('isActive')
  isActive!: boolean

  @JsonProperty('sortOrder')
  sortOrder!: number
}

/** On-hand data attached by the catalog when `stockBranchId` is supplied. */
export interface ProductStockSnapshot {
  quantity: number
  reorderLevel?: number
  lowStock?: boolean
}

@Serializable()
export class ProductRowEntity extends Fromable {
  @JsonProperty('id')
  id!: string

  @JsonProperty('title')
  title!: string

  @JsonProperty('skuCode')
  skuCode!: string

  /** POS scan code; empty when the product has none. Not unique, unlike skuCode. */
  @JsonProperty('barcode')
  barcode? = ''

  @JsonProperty('description')
  description = ''

  @JsonProperty('imageUrl')
  imageUrl?: string

  @JsonProperty('unit')
  unit!: ProductUnit

  @JsonProperty('isActive')
  isActive!: boolean

  @JsonProperty('paymentType')
  paymentType!: ProductPaymentType

  @JsonProperty('price')
  price!: number

  @JsonProperty('categoryId')
  categoryId?: number

  @JsonProperty('category')
  category?: ProductCategoryEntity

  /** Branches that sell this product; `[]` = every branch. */
  @JsonProperty('branchIds')
  branchIds: number[] = []

  /** @deprecated set only when the product sits on exactly one branch — read `branchIds`. */
  @JsonProperty('branchId')
  branchId?: number

  @JsonProperty('trackStock')
  trackStock = false

  @JsonProperty('stock')
  stock?: ProductStockSnapshot

  @JsonProperty('createdAt')
  createdAt!: string

  @JsonProperty('updatedAt')
  updatedAt!: string

  public getOption() {
    return { label: this.title, value: this.id }
  }

  public DataForTable() {
    return {
      id: this.id,
      title: this.title,
      skuCode: this.skuCode,
      paymentType: this.paymentType,
      price: this.price ? this.price.toLocaleString() : '-',
      isActive: this.isActive,
      updatedAt: this.updatedAt,
    }
  }
}

export interface ProductListQuery {
  query?: string
  paymentType?: ProductPaymentType
  branchId?: number
  /** Branch whose on-hand stock is attached as `product.stock`. */
  stockBranchId?: number
  status?: ProductStatus
  categoryId?: number
  page: number
  pageSize: number
}

export interface ProductPayload {
  title: string
  skuCode: string
  /** Sent on every write, so an emptied field clears the stored barcode. */
  barcode?: string
  description?: string
  imageUrl?: string
  unit: ProductUnit
  paymentType: ProductPaymentType
  price: number
  /** `true` (default) = the entered `price` already contains VAT. */
  priceIncludesVat?: boolean
  categoryId?: number
  /**
   * Branches that sell the product; `[]` = every branch. On update this REPLACES
   * the set, so `[]` puts the product back on every branch.
   */
  branchIds?: number[]
  trackStock: boolean
  isActive?: boolean
}

export type CreateProductDto = ProductPayload
export type UpdateProductDto = Partial<ProductPayload>

export interface CategoryPayload {
  name: string
  isActive?: boolean
  sortOrder?: number
}

export type StockMovementType = 'RECEIVE' | 'SALE' | 'ADJUST' | 'SALE_REVERSE'

/**
 * Stock value of one product at one branch.
 *
 * `avgCost` / `avgSalePrice` are `null` when there is nothing to average. The
 * dashboard renders that as `0` — a tracked product that never moved really is
 * worth 0 (QTY-2006) — while the null keeps "never averaged" distinguishable on
 * the wire. `quantityCosted < quantity` means part of the stock has no known
 * cost, so `stockValue` understates the real value. Money is in baht.
 */
@Serializable()
export class StockSummaryEntity extends Fromable {
  @JsonProperty('productId')
  productId!: string

  @JsonProperty('branchId')
  branchId!: number

  @JsonProperty('quantity')
  quantity = 0

  /** Part of `quantity` that has a known unit cost. */
  @JsonProperty('quantityCosted')
  quantityCosted = 0

  @JsonProperty('stockValue')
  stockValue = 0

  @JsonProperty('avgCost')
  avgCost: number | null = null

  @JsonProperty('avgSalePrice')
  avgSalePrice: number | null = null
}

@Serializable()
export class StockMovementEntity extends Fromable {
  @JsonProperty('id')
  id!: string

  @JsonProperty('productId')
  productId!: string

  @JsonProperty('branchId')
  branchId!: number

  @JsonProperty('type')
  type!: StockMovementType

  /** Signed: positive on receive/reverse, negative on sale/decrease. */
  @JsonProperty('quantityDelta')
  quantityDelta!: number

  @JsonProperty('balanceAfter')
  balanceAfter!: number

  /** Purchase cost per unit (baht) — `null` when unknown. */
  @JsonProperty('unitCost')
  unitCost: number | null = null

  /** Sale price per unit (baht) — `null` for non-sale rows. */
  @JsonProperty('unitPrice')
  unitPrice: number | null = null

  /** Supplier on a receive, buyer on a sale. */
  @JsonProperty('contact')
  contact = ''

  /** Staff member who recorded the movement. */
  @JsonProperty('actor')
  actor = ''

  @JsonProperty('refNo')
  refNo?: string

  @JsonProperty('note')
  note?: string

  @JsonProperty('createdAt')
  createdAt!: string
}

export interface StockMovementQuery {
  productId: string
  branchId: number
  page: number
  pageSize: number
}

export interface StockMovementPayload {
  productId: string
  branchId: number
  qty: number
  type: 'RECEIVE' | 'ADJUST'
  refNo?: string
  note?: string
  lotNo?: string
  expiryDate?: string
  /** Purchase cost per unit in baht — receives only. */
  unitCost?: number
  /** Supplier / contact name, free text. */
  contactName?: string
  /** 'YYYY-MM-DD' — defaults to today on the backend. */
  receivedAt?: string
}

export interface BulkReceiveRow {
  skuCode: string
  qty: number
  /** Purchase cost per unit in baht. */
  unitCost: number
}

export interface BulkReceivePayload {
  branchId: number
  contactName?: string
  /** 'YYYY-MM-DD' */
  receivedAt?: string
  refNo?: string
  note?: string
  rows: BulkReceiveRow[]
}

export interface BulkReceiveResult {
  applied: number
  failed: number
  results: { skuCode: string; ok: boolean; error?: string }[]
}

export interface ReorderLevelPayload {
  productId: string
  branchId: number
  reorderLevel: number
}

export interface LineDiscount {
  type: DiscountType
  value: number
}

export interface ProductSaleItemPayload {
  productId: string
  qty: number
  /** Purchase API accepts an amount in baht, not the POS display mode. */
  lineDiscount?: number
}

/**
 * Tax invoice details for a product sale — mirrors the backend
 * `CreateProductPurchaseTaxInvoiceDto`.
 *
 * NOT interchangeable with the package-purchase / credit-purchase tax invoice,
 * despite issuing the same document. Those endpoints take the address as
 * master-data ids (`provinceId`/`districtId`/`subDistrictId`) and let `taxNo`
 * double as a personal id; this one takes the resolved **names** and keeps the
 * personal id in `idNumber`. Sending the id-shaped payload here fails the
 * whitelist and used to surface as a blank error toast (QTY-1990).
 *
 * The backend additionally requires, for `INDIVIDUAL`: `firstName`, `lastName`,
 * `idType` and `idNumber` — and `country` when `idType` is `PASSPORT`. For a
 * legal entity it requires `companyName` and `taxNo`.
 */
export interface TaxInvoicePayload {
  businessType: TaxBusinessType
  idType?: TaxIdType
  /** Personal id (national id / passport no). Legal entities use `taxNo`. */
  idNumber?: string
  /** Nationality — required when `idType` is `PASSPORT`. */
  country?: string
  firstName?: string
  lastName?: string
  companyName?: string
  branch?: string
  taxNo?: string
  addressLine: string
  postalCode?: string
  province?: string
  district?: string
  subDistrict?: string
}

export interface CreateProductSalePayload {
  branchId: number
  memberId?: number
  cashierId?: number
  paymentType: ProductPaymentType
  receiptPaymentMethod?: ReceiptPaymentMethod
  paymentPlan?: PaymentPlan
  installmentPeriod?: number
  installmentRate?: number
  bankId?: number
  isTaxInvoiceRequired?: boolean
  paid?: number
  note?: string
  /** Serial of the POS machine this sale was rung up on. */
  posId?: string
  taxInvoice?: TaxInvoicePayload
  items: ProductSaleItemPayload[]
}

export interface ProductSaleItem {
  productId: string
  title: string
  skuCode: string
  unit: ProductUnit
  price: number
  qty: number
  trackStock: boolean
  lineDiscount?: LineDiscount
  discount: number
  total: number
}

export interface ProductSale {
  id: string
  receiptId: number
  receiptCode: string
  soldAt: string
  branchId: number
  branchName: string
  buyer?: {
    id?: number
    code?: string
    name: string
  }
  items: ProductSaleItem[]
  paymentType: ProductPaymentType
  receiptPaymentMethod?: ReceiptPaymentMethod
  subtotal: number
  discount: number
  vat: number
  total: number
  paid?: number
  change?: number
  remainingCredit?: number
  status: ProductSaleStatus
  voidReason?: string
  voidEligibleUntil: string
  canVoid: boolean
}

export interface ProductSalesQuery {
  branchId: number
  query?: string
  paymentType?: ProductPaymentType
  status?: ProductSaleStatus
  page: number
  pageSize: number
}

export interface ProductSalesResult extends PageResult<ProductSale> {
  serverTime: string
}
